import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { createContext, useContext } from "react";
import { AccountModel, LockStatusResult, UserProfileModel } from "../types/models";
import { useUserDetailQuery, useUnlockAccountMutation } from "../hooks/AccountHooks";

const DEFAULT_RESOURCE_ID = 8;

export interface accountContext {
    user: UserProfileModel;
    accounts: AccountModel[];
    selectedAccountId: number;
    selectedAccount: AccountModel | undefined;
    setSelectedAccountByResourceId: (resourceId: number) => void;
    isAccountSwitcherOpen: boolean;
    setIsAccountSwitcherOpen: (isOpen: boolean) => void;
    setSwitchAccountCallback: (callback: () => void) => void;
    displayName: string;
    lockStatus: LockStatusResult;
    unlockAccount: (resourceId?: number) => void;
}

export const DEFAULT_USER_PROFILE: UserProfileModel = {
    accounts: [],
    currentAccountId: 0,
    fullName: '',
    firstName: '',
    lastName: '',
} as const;

export const AccountContext = createContext<accountContext>({
    user: { ...DEFAULT_USER_PROFILE },
    accounts: [],
    selectedAccountId: 0,
    selectedAccount: undefined,
    setSelectedAccountByResourceId: function (): void {
        throw new Error("Function not implemented.");
    },
    isAccountSwitcherOpen: false,
    setIsAccountSwitcherOpen: function (): void {
        throw new Error("Function not implemented.");
    },
    setSwitchAccountCallback: function (): void {
        throw new Error("Function not implemented.");
    },
    displayName: '',
    lockStatus: LockStatusResult.Pending,
    unlockAccount: function (): void {
        throw new Error("Function not implemented.");
    },
});

export const useAccounts = () => useContext(AccountContext);

export const setDisplayNameCapitalization = (name: string): string => {
    return name
        .toLowerCase()
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.substring(1))
        .join(' ');
};

export const AccountProvider = ({ children }: React.PropsWithChildren<{}>) => {
    const { data: userDetail } = useUserDetailQuery();
    const [user, setUser] = useState<UserProfileModel>({ ...DEFAULT_USER_PROFILE });
    const [displayName, setDisplayName] = useState<string>('');
    const [lockStatus, setLockStatus] = useState<LockStatusResult>(LockStatusResult.Pending);
    const [isAccountSwitcherOpen, setIsAccountSwitcherOpen] = useState(false);

    const unlockMutation = useUnlockAccountMutation();

    useEffect(() => {
        if (userDetail?.isAuthenticated === true && userDetail.userProfile !== null) {
            const userAccounts = (userDetail.accounts ?? []).map(a => ({ ...a, isLocked: null as boolean | null }));
            const defaultId = userAccounts.find(a => a.resourceId === DEFAULT_RESOURCE_ID)?.resourceId
                ?? userAccounts[0]?.resourceId
                ?? 0;
            setUser({
                accounts: userAccounts,
                currentAccountId: defaultId,
                fullName: userDetail.userProfile.fullName,
                firstName: userDetail.userProfile.firstName,
                lastName: userDetail.userProfile.lastName,
            });
            setDisplayName(setDisplayNameCapitalization(userDetail.userProfile.fullName));
        }
    }, [userDetail]);

    const unlockAccount = useCallback(async (resourceId?: number) => {
        const targetResourceId = resourceId ?? user.currentAccountId;
        try {
            const data = await unlockMutation.mutateAsync(targetResourceId);

            let nextStatus: LockStatusResult;
            let nextIsLocked: boolean | null;
            switch (data.toLowerCase()) {
                case "not locked": {
                    nextStatus = LockStatusResult.NoAction;
                    nextIsLocked = false;
                    break;
                }
                case "unlocked": {
                    nextStatus = LockStatusResult.Unlocked;
                    nextIsLocked = false;
                    break;
                }
                default: {
                    nextStatus = LockStatusResult.Error;
                    nextIsLocked = null;
                    break;
                }
            }

            setLockStatus(nextStatus);
            setUser(prev => ({
                ...prev,
                accounts: prev.accounts.map(a => a.resourceId === targetResourceId ? { ...a, isLocked: nextIsLocked } : a),
            }));
        } catch (error) {
            setLockStatus(LockStatusResult.Error);
        }
    // Depend on mutateAsync itself (stable across renders), not the whole mutation
    // result object - that object's identity changes with every status transition
    // (idle -> pending -> success), which would otherwise re-trigger any effect that
    // lists unlockAccount as a dependency on every call, looping indefinitely.
    }, [user.currentAccountId, unlockMutation.mutateAsync]);

    // an imperative registration slot, not render state - a ref avoids re-rendering (and looping) on every registration
    const switchAccountCallbackRef = useRef<() => void>(() => {});
    const setSwitchAccountCallback = useCallback((callback: () => void) => {
        switchAccountCallbackRef.current = callback;
    }, []);

    // user.currentAccountId is the single source of truth for "which account is selected" -
    // avoid tracking a second id that could drift out of sync with it.
    const setSelectedAccountByResourceId = async (resourceId: number) => {
        setUser(prev => ({ ...prev, currentAccountId: resourceId }));

        // used to clear local state when the account switches
        switchAccountCallbackRef.current();

        await unlockAccount(resourceId);
    };

    const selectedAccount = user.accounts.find(a => a.resourceId === user.currentAccountId);

    const contextValue = useMemo(() => ({
            user,
            accounts: user.accounts,
            selectedAccountId: user.currentAccountId,
            selectedAccount,
            setSelectedAccountByResourceId,
            isAccountSwitcherOpen,
            setIsAccountSwitcherOpen,
            setSwitchAccountCallback,
            displayName,
            lockStatus,
            unlockAccount,
    }), [
        user,
        selectedAccount,
        isAccountSwitcherOpen,
        displayName,
        lockStatus,
        unlockAccount,
    ]);

    return (
        <AccountContext.Provider value={contextValue}>
            {children}
        </AccountContext.Provider>
    );
};
